import { useMemo, useState } from 'react';
import { DecimalInput } from '../ui/DecimalInput';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, Check, Copy, FlaskConical, LayoutTemplate, Pencil, Play, Plus, RotateCcw, Trash2, Wrench, X, Zap } from 'lucide-react';
import {
  ACTION_LABEL,
  ACTION_TYPES_EDITOR,
  ACTOR_KINDS,
  ACTOR_KIND_LABEL,
  type ActorRef,
  AUTOMATION_MAX_ACTIONS,
  AUTOMATION_MAX_PREDICATES,
  byPosition,
  describeAutomation,
  FIELD_TEST_OPS,
  fieldTestKind,
  fieldTestLabel,
  isGroup,
  PREDICATE_LABEL,
  PREDICATE_TYPES,
  PRIORITIES,
  roleAtLeast,
  RULE_TEMPLATES,
  TRIGGER_GROUPS,
  TRIGGER_LABEL,
  type Automation,
  type AutomationAction,
  type AutomationPredicate,
  type AutomationTrigger,
  type BoardSnapshot,
  type ConditionGroup,
  type CustomField,
  type DescribeContext,
  type FieldTest,
  type FileKind,
  HOLIDAY_REGIONS,
  type HolidayRegion,
} from '@kaba/shared';
import { api, ApiError } from '../../lib/api';
import { useBoard, useBoards } from '../../lib/board';
import { useReminderTargets } from '../../lib/reminders';
import { accountPref } from '../../lib/boardNav';
import { toast } from '../../lib/toast';
import { HolidaySelect } from '../HolidaySelect';
import { Button, cx, Sheet } from '../ui/ui';

interface Run {
  id: number;
  automationId: string;
  cardId: string | null;
  cardTitle: string | null;
  ok: boolean;
  message: string | null;
  event: string | null;
  result?: string | null;
  at: string;
}

/** 0.63: Ergebnis eines Laufs – getrennt nach ausgeführt, ohne Änderung, wartend, verpasst, abgebrochen, gesperrt, Fehler */
const RESULT: Record<string, { label: string; dot: string }> = {
  changed: { label: 'ausgeführt', dot: 'bg-emerald-500' },
  unchanged: { label: 'ohne Änderung', dot: 'bg-slate-400' },
  waiting: { label: 'wartet', dot: 'bg-amber-500' },
  missed: { label: 'verpasst', dot: 'bg-amber-500' },
  aborted: { label: 'abgebrochen', dot: 'bg-rose-600' },
  blocked: { label: 'gesperrt', dot: 'bg-rose-600' },
  failed: { label: 'Fehler', dot: 'bg-rose-600' },
};
const resultOf = (r: Run) => RESULT[r.result ?? (r.ok ? 'changed' : 'failed')] ?? RESULT.failed!;

interface Preview {
  problems: string[];
  archived: boolean;
  conditionsMet: boolean;
  conditions: { text: string; ok: boolean; depth: number; note?: string | null }[];
  actions: { text: string; changes: boolean | null; result?: string; recipients?: string[]; note?: string }[];
  warnings?: string[];
  recipients: string[];
  stateNote?: string | null;
  /** 0.61: konkreter Zeitpunkt eines Termin-Auslösers für diese Karte */
  timing?: string | null;
  timeZone?: string;
}

/** 0.61: Grund eines Laufs im Protokoll */
const RUN_REASON: Record<string, string> = {
  stateMet: 'weil die Bedingungen erfüllt wurden',
  apply: 'auf bestehende Karte angewendet',
  dateReached: 'zum geplanten Zeitpunkt',
  dateChanged: 'weil Start oder Frist geändert wurde',
  effortChanged: 'weil sich Schätzung oder gebuchte Zeit geändert hat',
  predecessorsDone: 'weil alle Vorgänger erledigt sind',
  ready: 'weil die Karte startbereit wurde',
  manual: 'per Knopf an der Karte',
};

const sel = 'min-h-11 max-w-full min-w-0 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-base sm:min-h-9 sm:text-sm';
const iconBtn = 'inline-flex size-11 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] hover:bg-black/5 sm:size-9 dark:hover:bg-white/10';
const timeAgo = (iso: string) => new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
const FILE_KINDS: { value: FileKind; label: string }[] = [
  { value: 'any', label: 'beliebige Datei' },
  { value: 'pdf', label: 'PDF-Datei' },
  { value: 'image', label: 'Bild' },
  { value: 'other', label: 'sonstige Datei' },
];

export function useDescribeContext(snapshot: BoardSnapshot): DescribeContext {
  const { data: targets } = useReminderTargets(snapshot.board.id, true);
  return useMemo(
    () => ({
      column: (id) => snapshot.columns.find((c) => c.id === id)?.title,
      label: (id) => {
        const l = snapshot.labels.find((x) => x.id === id);
        return l ? l.name || l.color : undefined;
      },
      person: (id) => snapshot.members.find((m) => m.id === id)?.name,
      field: (id) => snapshot.fields.find((f) => f.id === id),
      group: (id) => [...(targets?.groups ?? []), ...(targets?.named ?? [])].find((g) => g.id === id)?.name,
    }),
    [snapshot.columns, snapshot.labels, snapshot.members, snapshot.fields, targets],
  );
}

export function AutomationsSheet({ snapshot, open, onClose }: { snapshot: BoardSnapshot; open: boolean; onClose: () => void }) {
  const boardId = snapshot.board.id;
  const canEdit = roleAtLeast(snapshot.board.role, 'editor');
  const qc = useQueryClient();
  const ctx = useDescribeContext(snapshot);
  const { data } = useQuery({
    queryKey: ['automations', boardId],
    queryFn: () => api.get<{ automations: Automation[]; runs: Run[] }>(`/api/boards/${boardId}/automations`),
    enabled: open,
  });
  const [editing, setEditing] = useState<Automation | 'new' | null>(null);
  const [applying, setApplying] = useState<Automation | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'paused' | 'repair'>('all');
  const [cat, setCat] = useState('');
  const shown = (data?.automations ?? []).filter(
    (a) =>
      (filter === 'all' || (filter === 'active' ? a.enabled && !a.problems.length : filter === 'paused' ? !a.enabled : a.problems.length > 0)) &&
      (!cat || TRIGGER_GROUPS.find((g) => g.label === cat)?.types.includes(a.trigger.type)),
  );
  const refresh = () => qc.invalidateQueries({ queryKey: ['automations', boardId] });
  const names = new Map(data?.automations.map((a) => [a.id, a.name]));

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setEditing(null);
          onClose();
        }
      }}
      title={editing ? (editing === 'new' ? 'Neue Regel' : 'Regel bearbeiten') : 'Automationen'}
    >
      {editing ? (
        <RuleEditor
          snapshot={snapshot}
          initial={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : (
        <div className="flex flex-col gap-4 p-4">
          <p className="text-sm text-[var(--ink-soft)]">
            Regeln erledigen Routinearbeit von selbst: „Wenn eine Karte nach Erledigt kommt, hake sie ab.“ Sie laufen auf dem Server, für alle im
            Board, und stehen im Verlauf der Karte.
          </p>
          {canEdit && (
            <Button variant="primary" onClick={() => setEditing('new')} className="self-start">
              <Plus size={16} /> Neue Regel
            </Button>
          )}
          {data && !data.automations.length && <p className="text-sm text-[var(--ink-soft)]">Noch keine Regeln.</p>}
          {data && data.automations.length > 3 && (
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Regeln filtern">
              <select aria-label="Zustand" className={sel} value={filter} onChange={(e) => setFilter(e.target.value as 'all')}>
                <option value="all">Alle Regeln</option>
                <option value="active">Aktiv</option>
                <option value="paused">Pausiert</option>
                <option value="repair">Muss repariert werden</option>
              </select>
              <select aria-label="Auslöser" className={sel} value={cat} onChange={(e) => setCat(e.target.value)}>
                <option value="">Alle Auslöser</option>
                {TRIGGER_GROUPS.map((g) => (
                  <option key={g.label} value={g.label}>
                    {g.label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-[var(--ink-soft)]">
                {shown.length} von {data.automations.length}
              </span>
            </div>
          )}
          <ul className="flex flex-col gap-2">
            {shown.map((a) => (
              <li key={a.id} className={cx('flex flex-col gap-2 rounded-xl border p-3', a.enabled ? 'border-[var(--line)]' : 'border-dashed border-[var(--line)] opacity-70')}>
                <div className="flex items-start gap-2">
                  <Zap size={16} className={cx('mt-0.5 shrink-0', a.enabled ? 'text-violet-600' : 'text-[var(--ink-soft)]')} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{a.name}</p>
                    <p className="text-sm text-[var(--ink-soft)]">{describeAutomation(a, ctx)}</p>
                    <p className="mt-1 text-xs text-[var(--ink-soft)]">
                      {a.runCount ? `${a.runCount}-mal ausgeführt` : 'Noch nicht ausgeführt'}
                      {a.lastRunAt && ` · zuletzt ${timeAgo(a.lastRunAt)}`}
                    </p>
                    {a.enabled && a.planned && (a.planned.count > 0 || a.planned.missed > 0 || a.trigger.type === 'dateReached') && (
                      <p className="text-xs text-[var(--ink-soft)]">
                        {a.trigger.type === 'dateReached'
                          ? a.planned.count
                            ? `Geplant für ${a.planned.count === 1 ? '1 Karte' : `${a.planned.count} Karten`}${a.planned.next ? ` · nächster Zeitpunkt ${timeAgo(a.planned.next)}` : ''}`
                            : 'Derzeit kein Zeitpunkt geplant (keine offene Karte mit passendem Termin).'
                          : a.planned.next
                            ? `Nächste Prüfung ${timeAgo(a.planned.next)}`
                            : ''}
                        {a.planned.missed > 0 && ` · ${a.planned.missed} verpasst (Server nicht erreichbar, nicht nachgeholt)`}
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <label className="flex min-h-10 items-center gap-1.5 text-xs">
                      <input
                        type="checkbox"
                        role="switch"
                        aria-label={`Regel „${a.name}“ aktiv`}
                        checked={a.enabled}
                        onChange={async (e) => {
                          const enabled = e.target.checked;
                          await api.patch(`/api/automations/${a.id}`, { enabled });
                          void refresh();
                        }}
                        className="size-5 accent-[var(--color-accent)]"
                      />
                      aktiv
                    </label>
                  )}
                </div>
                {a.problems.length > 0 && (
                  <div className="flex flex-wrap items-start gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100" role="alert">
                    <Wrench size={14} className="mt-px shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <b>Muss repariert werden – läuft bis dahin nicht.</b> {a.problems.join(' ')}
                    </span>
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>
                        Reparieren
                      </Button>
                    )}
                  </div>
                )}
                {a.lastError && !a.problems.length && (
                  <p className="flex items-start gap-1.5 rounded-lg bg-rose-50 p-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">
                    <AlertTriangle size={14} className="mt-px shrink-0" /> Letzter Lauf fehlgeschlagen: {a.lastError}
                  </p>
                )}
                {canEdit && (
                  <div className="flex flex-wrap justify-end gap-1">
                    {a.mode === 'state' && a.enabled && !a.problems.length && (
                      <Button size="sm" variant="ghost" onClick={() => setApplying(a)}>
                        <Play size={14} /> Auf bestehende Karten anwenden …
                      </Button>
                    )}
                    {a.mode === 'state' && a.once && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (!window.confirm(`„Nur einmal je Karte“ für „${a.name}“ zurücksetzen? Danach kann die Regel auf jeder Karte wieder einmal laufen.`)) return;
                          try {
                            const r = await api.post<{ reset: number }>(`/api/automations/${a.id}/reset-once`, {});
                            toast.info(`Zurückgesetzt (${r.reset} ${r.reset === 1 ? 'Karte' : 'Karten'}).`);
                          } catch (err) {
                            toast.error((err as Error).message);
                          }
                        }}
                      >
                        <RotateCcw size={14} /> Einmal-Sperre zurücksetzen
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>
                      <Pencil size={14} /> Bearbeiten
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        try {
                          await api.post(`/api/boards/${boardId}/automations`, {
                            name: `${a.name} (Kopie)`.slice(0, 80),
                            enabled: false,
                            schemaVersion: 2,
                            trigger: a.trigger,
                            when: a.when,
                            actions: a.actions,
                            once: a.once,
                          });
                          toast.info('Kopie angelegt – sie ist pausiert, bis du sie einschaltest.');
                          void refresh();
                        } catch (err) {
                          toast.error((err as Error).message);
                        }
                      }}
                    >
                      <Copy size={14} /> Duplizieren
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        if (!window.confirm(`Regel „${a.name}“ löschen?`)) return;
                        await api.del(`/api/automations/${a.id}`);
                        void refresh();
                      }}
                    >
                      <Trash2 size={14} /> Löschen
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <ApplyDialog rule={applying} ctx={ctx} onClose={() => setApplying(null)} onDone={() => void refresh()} />
          {!!data?.runs.length && (
            <section className="flex flex-col gap-1.5">
              <h3 className="text-sm font-semibold">Zuletzt ausgeführt</h3>
              <ul className="flex flex-col divide-y divide-[var(--line)] text-xs">
                {data.runs.map((r) => (
                  <li key={r.id} className="flex items-center gap-2 py-1.5">
                    <span className={cx('size-2 shrink-0 rounded-full', resultOf(r).dot)} aria-hidden />
                    <span className="sr-only">{resultOf(r).label}:</span>
                    <span className="min-w-0 flex-1 truncate">
                      <b>{names.get(r.automationId) ?? 'Regel'}</b>
                      {r.cardTitle && ` · „${r.cardTitle}“`}
                      {r.event && r.event in RUN_REASON
                        ? ` · ${RUN_REASON[r.event]}`
                        : r.event && r.event in TRIGGER_LABEL && ` · weil ${TRIGGER_LABEL[r.event as AutomationTrigger['type']]}`}
                      {r.message && ` · ${r.message}`}
                    </span>
                    <span className="shrink-0 text-[var(--ink-soft)]">{timeAgo(r.at)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Sheet>
  );
}

/**
 * 0.60: Zustandsregel auf Karten anwenden, die schon passen – Trefferliste, nichts vorausgewählt,
 * ausdrückliche Bestätigung. Karten, die inzwischen nicht mehr passen, überspringt der Server.
 */
function ApplyDialog({ rule, ctx, onClose, onDone }: { rule: Automation | null; ctx: DescribeContext; onClose: () => void; onDone: () => void }) {
  const q = useQuery({
    queryKey: ['automation-matches', rule?.id, rule?.revision],
    queryFn: () => api.get<{ cards: { id: string; title: string; columnId: string }[] }>(`/api/automations/${rule!.id}/matches`),
    enabled: !!rule,
  });
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const cards = q.data?.cards ?? [];
  return (
    <Sheet
      open={!!rule}
      onOpenChange={(o) => {
        if (!o) {
          setPicked([]);
          onClose();
        }
      }}
      title={rule ? `„${rule.name}“ auf bestehende Karten anwenden` : 'Anwenden'}
      side="center"
    >
      <div className="flex flex-col gap-3 p-4">
        {rule && <p className="text-sm text-[var(--ink-soft)]">{describeAutomation(rule, ctx)}</p>}
        {q.isLoading ? (
          <p className="text-sm text-[var(--ink-soft)]">Lädt …</p>
        ) : cards.length ? (
          <>
            <p className="text-sm">
              {cards.length === 1 ? '1 Karte erfüllt' : `${cards.length} Karten erfüllen`} die Bedingungen schon, ohne dass die Regel dort lief. Wähle aus,
              wo sie jetzt einmal ausgeführt werden soll.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setPicked(picked.length === cards.length ? [] : cards.map((c) => c.id))}>
                {picked.length === cards.length ? 'Keine' : 'Alle'} auswählen
              </Button>
            </div>
            <ul className="flex max-h-72 flex-col overflow-y-auto rounded-xl border border-[var(--line)]" aria-label="Passende Karten">
              {cards.map((c) => (
                <li key={c.id} className="border-b border-[var(--line)] last:border-b-0">
                  <label className="flex min-h-11 cursor-pointer items-center gap-3 px-3">
                    <input
                      type="checkbox"
                      className="size-5 accent-[var(--color-accent)]"
                      checked={picked.includes(c.id)}
                      onChange={(e) => setPicked((p) => (e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id)))}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm">{c.title}</span>
                    <span className="shrink-0 text-xs text-[var(--ink-soft)]">{ctx.column(c.columnId)}</span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-sm">Keine Karte erfüllt die Bedingungen, ohne dass die Regel dort schon lief.</p>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={!picked.length || busy || !rule}
            onClick={async () => {
              if (!rule) return;
              if (!window.confirm(`Regel jetzt auf ${picked.length === 1 ? '1 Karte' : `${picked.length} Karten`} anwenden?`)) return;
              setBusy(true);
              try {
                const r = await api.post<{ done: number; skipped: number; failed: number }>(`/api/automations/${rule.id}/apply`, { cardIds: picked, expectedRevision: rule.revision });
                toast.info(
                  `${r.done === 1 ? '1 Karte' : `${r.done} Karten`} bearbeitet${r.skipped ? `, ${r.skipped} übersprungen (passen nicht mehr)` : ''}${r.failed ? `, ${r.failed} fehlgeschlagen – siehe Protokoll` : ''}.`,
                );
                setPicked([]);
                onDone();
                onClose();
              } catch (err) {
                toast.error((err as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Ausführen{picked.length ? ` (${picked.length})` : ''}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

/* ---------- Bausteine ---------- */

const labelName = (l: BoardSnapshot['labels'][number]) => l.name || l.color;

/** Mehrfachauswahl als Chips (Tastatur: Checkboxen) */
function ChipPicker({ label, options, value, onChange }: { label: string; options: { id: string; name: string }[]; value: string[]; onChange: (v: string[]) => void }) {
  if (!options.length) return <span className="text-sm text-[var(--ink-soft)]">Keine vorhanden.</span>;
  return (
    <fieldset className="flex flex-wrap gap-1.5">
      <legend className="sr-only">{label}</legend>
      {options.map((o) => {
        const on = value.includes(o.id);
        return (
          <label
            key={o.id}
            className={cx(
              'inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-sm sm:min-h-9',
              on ? 'border-accent bg-accent-soft font-medium' : 'border-[var(--line)] hover:bg-black/5 dark:hover:bg-white/10',
            )}
          >
            <input type="checkbox" className="sr-only" checked={on} onChange={() => onChange(on ? value.filter((x) => x !== o.id) : [...value, o.id])} />
            {on && <Check size={14} aria-hidden />}
            {o.name}
          </label>
        );
      })}
    </fieldset>
  );
}

function FieldTestEditor({ field, test, onChange, members }: { field: CustomField | undefined; test: FieldTest; onChange: (t: FieldTest) => void; members: BoardSnapshot['members'] }) {
  if (!field) return <span className="text-sm text-rose-600">Feld fehlt</span>;
  const kind = fieldTestKind(field.type);
  const ops = FIELD_TEST_OPS[kind];
  const needsValue = !['empty', 'filled', 'checked', 'unchecked', 'anyOf', 'allOf', 'noneOf', 'approved', 'notApproved', 'rejected'].includes(test.op);
  const valueInput = (v: FieldTest['value'], set: (x: string | number | null) => void, aria: string) =>
    kind === 'person' ? (
      <select aria-label={aria} className={sel} value={String(v ?? '')} onChange={(e) => set(e.target.value || null)}>
        <option value="">Person wählen …</option>
        {members.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>
    ) : kind === 'number' ? (
      <DecimalInput aria-label={aria} className={cx(sel, 'w-28')} value={typeof v === 'number' ? v : null} onChange={set} />
    ) : (
      <input
        aria-label={aria}
        className={cx(sel, 'min-w-0 flex-1')}
        type={kind === 'date' ? 'date' : 'text'}
        value={v === null || v === undefined ? '' : String(v)}
        onChange={(e) => set(e.target.value)}
      />
    );
  return (
    <>
      <select
        aria-label="Vergleich"
        className={sel}
        value={test.op}
        onChange={(e) => {
          const op = e.target.value as FieldTest['op'];
          onChange({ op, ...(op === 'anyOf' || op === 'allOf' || op === 'noneOf' ? { optionIds: test.optionIds ?? [] } : { value: test.value ?? null }) });
        }}
      >
        {ops.map((op) => (
          <option key={op} value={op}>
            {fieldTestLabel(kind, op)}
          </option>
        ))}
      </select>
      {(test.op === 'anyOf' || test.op === 'allOf' || test.op === 'noneOf') && (
        <ChipPicker label={`Auswahl von ${field.name}`} options={field.options.map((o) => ({ id: o.id, name: o.label }))} value={test.optionIds ?? []} onChange={(optionIds) => onChange({ ...test, optionIds })} />
      )}
      {needsValue && valueInput(test.value, (value) => onChange({ ...test, value }), 'Vergleichswert')}
      {test.op === 'between' && (
        <>
          <span className="text-sm">und</span>
          {valueInput(test.value2, (value2) => onChange({ ...test, value2 }), 'Zweiter Vergleichswert')}
        </>
      )}
    </>
  );
}

const defaultTest = (f: CustomField | undefined): FieldTest => ({ op: f ? FIELD_TEST_OPS[fieldTestKind(f.type)][0]! : 'filled' });

function defaultPredicate(type: (typeof PREDICATE_TYPES)[number], s: BoardSnapshot): AutomationPredicate {
  const cols = [...s.columns].sort(byPosition);
  switch (type) {
    case 'labels':
      return { type, mode: 'any', labelIds: s.labels[0] ? [s.labels[0].id] : [] };
    case 'column':
      return { type, mode: 'in', columnIds: cols[0] ? [cols[0].id] : [] };
    case 'done':
      return { type, value: false };
    case 'priority':
      return { type, op: 'gte', value: 3 };
    case 'assignees':
      return { type, mode: 'none' };
    case 'blocked':
      return { type, value: true };
    case 'files':
      return { type, kind: 'any', includeComments: false, min: 1 };
    case 'field':
      return { type, fieldId: s.fields[0]?.id ?? '', test: defaultTest(s.fields[0]) };
    case 'dates':
      return { type, which: 'due', op: 'missing' };
    case 'checklist':
      return { type, op: 'complete' };
    case 'dateWindow':
      return { type, which: 'due', op: 'within', minutes: 2 * 1440 };
    case 'effort':
      return { type, measure: 'percent', op: 'gte', value: 80, includeRunning: true };
    case 'estimate':
      return { type, op: 'missing' };
    case 'predecessors':
      return { type, op: 'allDone' };
    case 'ready':
      return { type, value: true };
    case 'dwell':
      return { type, op: 'atLeast', minutes: 3 * 1440 };
    case 'inactive':
      return { type, op: 'atLeast', minutes: 7 * 1440 };
    case 'actor':
      return { type, mode: 'isNot', who: { kind: 'creator' } };
  }
}

/** 0.81: Folgekarte – Ziel (dieses oder anderes Board), Titel mit {titel}, Übernahme */
function CreateCardEditor({ a, s, onChange }: { a: Extract<AutomationAction, { type: 'createCard' }>; s: BoardSnapshot; onChange: (a: AutomationAction) => void }) {
  const { data: boards } = useBoards();
  const targetId = a.boardId ?? s.board.id;
  const { data: target } = useBoard(targetId);
  const cols = [...(targetId === s.board.id ? s.columns : (target?.columns ?? []))].sort(byPosition);
  const writable = (boards ?? []).filter((b) => !b.archivedAt && (b.role === 'owner' || b.role === 'editor'));
  const copy = (k: keyof typeof a.copy, label: string) => (
    <label className="flex min-h-10 items-center gap-1.5 text-sm">
      <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={a.copy[k]} onChange={(e) => onChange({ ...a, copy: { ...a.copy, [k]: e.target.checked } })} />
      {label}
    </label>
  );
  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm">in</span>
        <select
          aria-label="Ziel-Board"
          className={sel}
          value={targetId}
          onChange={(e) => onChange({ ...a, boardId: e.target.value === s.board.id ? null : e.target.value, columnId: '' })}
        >
          <option value={s.board.id}>diesem Board</option>
          {writable
            .filter((b) => b.id !== s.board.id)
            .map((b) => (
              <option key={b.id} value={b.id}>
                {b.title}
              </option>
            ))}
        </select>
        <select aria-label="Spalte der Folgekarte" className={sel} value={a.columnId} onChange={(e) => onChange({ ...a, columnId: e.target.value })}>
          {!cols.some((c) => c.id === a.columnId) && <option value={a.columnId}>Spalte wählen …</option>}
          {cols.map((c) => (
            <option key={c.id} value={c.id}>
              „{c.title}“
            </option>
          ))}
        </select>
      </div>
      <input aria-label="Titel der Folgekarte" className={cx(sel, 'w-full')} maxLength={200} value={a.title} onChange={(e) => onChange({ ...a, title: e.target.value })} />
      <span className="text-xs text-[var(--ink-soft)]">{'{titel}'} wird durch den Titel der auslösenden Karte ersetzt. Übernommen werden gleichnamige Felder und Labels des Ziel-Boards.</span>
      <div className="flex flex-wrap gap-x-4">
        {copy('fields', 'Felder')}
        {copy('labels', 'Labels')}
        {copy('description', 'Beschreibung')}
        {copy('assignees', 'Zuständige')}
      </div>
      {a.boardId && <span className="text-xs text-[var(--ink-soft)]">Läuft mit deinen Rechten im Ziel-Board. Diese Regel wird nicht in Vorlagen oder Board-Dateien übernommen.</span>}
    </div>
  );
}

/** 0.79: „Die auslösende Person ist (nicht) …“ */
function ActorEditor({ p, s, onChange }: { p: Extract<AutomationPredicate, { type: 'actor' }>; s: BoardSnapshot; onChange: (p: AutomationPredicate) => void }) {
  const { data: targets } = useReminderTargets(s.board.id, true);
  const cols = [...s.columns].sort(byPosition);
  const w = p.who;
  const groups = targets?.groups ?? [];
  const approvals = s.fields.filter((f) => f.type === 'approval');
  const pick = (kind: ActorRef['kind']): ActorRef => {
    switch (kind) {
      case 'movedTo':
        return { kind, columnId: cols[0]?.id ?? '' };
      case 'fieldSetBy':
        return { kind, fieldId: s.fields[0]?.id ?? '' };
      case 'approvedBy':
        return { kind, fieldId: approvals[0]?.id ?? '' };
      case 'user':
        return { kind, userId: s.members[0]?.id ?? '' };
      case 'group':
        return { kind, groupId: groups[0]?.id ?? '' };
      default:
        return { kind } as ActorRef;
    }
  };
  return (
    <>
      <select aria-label="Person: ist oder ist nicht" className={sel} value={p.mode} onChange={(e) => onChange({ ...p, mode: e.target.value as 'is' })}>
        <option value="isNot">ist nicht</option>
        <option value="is">ist</option>
      </select>
      <select aria-label="Person: wer" className={sel} value={w.kind} onChange={(e) => onChange({ ...p, who: pick(e.target.value as ActorRef['kind']) })}>
        {ACTOR_KINDS.filter((k) => (k !== 'fieldSetBy' || s.fields.length || w.kind === k) && (k !== 'approvedBy' || approvals.length || w.kind === k) && (k !== 'group' || groups.length || w.kind === k)).map((k) => (
          <option key={k} value={k}>
            {ACTOR_KIND_LABEL[k]}
          </option>
        ))}
      </select>
      {w.kind === 'movedTo' && (
        <select aria-label="Spalte" className={sel} value={w.columnId} onChange={(e) => onChange({ ...p, who: { kind: 'movedTo', columnId: e.target.value } })}>
          {!cols.some((c) => c.id === w.columnId) && <option value={w.columnId}>(gelöschte Spalte)</option>}
          {cols.map((c) => (
            <option key={c.id} value={c.id}>
              nach „{c.title}“
            </option>
          ))}
        </select>
      )}
      {w.kind === 'fieldSetBy' && (
        <select aria-label="Feld" className={sel} value={w.fieldId} onChange={(e) => onChange({ ...p, who: { kind: 'fieldSetBy', fieldId: e.target.value } })}>
          {!s.fields.some((f) => f.id === w.fieldId) && <option value={w.fieldId}>(gelöschtes Feld)</option>}
          {s.fields.map((f) => (
            <option key={f.id} value={f.id}>
              „{f.name}“
            </option>
          ))}
        </select>
      )}
      {w.kind === 'approvedBy' && (
        <select aria-label="Freigabefeld" className={sel} value={w.fieldId} onChange={(e) => onChange({ ...p, who: { kind: 'approvedBy', fieldId: e.target.value } })}>
          {!approvals.some((f) => f.id === w.fieldId) && <option value={w.fieldId}>(gelöschtes Feld)</option>}
          {approvals.map((f) => (
            <option key={f.id} value={f.id}>
              bei „{f.name}“
            </option>
          ))}
        </select>
      )}
      {w.kind === 'user' && (
        <select aria-label="Person" className={sel} value={w.userId} onChange={(e) => onChange({ ...p, who: { kind: 'user', userId: e.target.value } })}>
          {!s.members.some((m) => m.id === w.userId) && <option value={w.userId}>(nicht mehr im Board)</option>}
          {s.members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      )}
      {w.kind === 'group' && (
        <select aria-label="Gruppe" className={sel} value={w.groupId} onChange={(e) => onChange({ ...p, who: { kind: 'group', groupId: e.target.value } })}>
          {!groups.some((g) => g.id === w.groupId) && <option value={w.groupId}>(Gruppe nicht verfügbar)</option>}
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      )}
    </>
  );
}


/* ---------- 0.61: Dauer, Termine, Aufwand ---------- */

const DURATION_UNITS = [
  { value: 1, label: 'Minuten' },
  { value: 60, label: 'Stunden' },
  { value: 1440, label: 'Tage (24 Std.)' },
] as const;
const unitFor = (m: number) => (m && m % 1440 === 0 ? 1440 : m && m % 60 === 0 ? 60 : 1);

/** Eine Dauer in Minuten als Zahl + Einheit */
function DurationInput({ label, minutes, onChange, units = DURATION_UNITS }: { label: string; minutes: number; onChange: (m: number) => void; units?: readonly { value: number; label: string }[] }) {
  const [unit, setUnit] = useState(() => {
    const u = unitFor(minutes);
    return units.some((x) => x.value === u) ? u : units[0]!.value;
  });
  return (
    <>
      <input
        aria-label={label}
        type="number"
        min={1}
        max={9999}
        className={cx(sel, 'w-20')}
        value={Math.max(1, Math.round(minutes / unit))}
        onChange={(e) => onChange(Math.max(1, Math.min(9999, Math.floor(Number(e.target.value) || 1))) * unit)}
      />
      <select
        aria-label={`${label}: Einheit`}
        className={sel}
        value={unit}
        onChange={(e) => {
          const u = Number(e.target.value);
          setUnit(u);
          onChange(Math.max(1, Math.round(minutes / unit)) * u);
        }}
      >
        {units.map((u) => (
          <option key={u.value} value={u.value}>
            {u.label}
          </option>
        ))}
      </select>
    </>
  );
}

function WhichSelect({ value, onChange, label = 'Termin' }: { value: 'start' | 'due'; onChange: (v: 'start' | 'due') => void; label?: string }) {
  return (
    <select aria-label={label} className={sel} value={value} onChange={(e) => onChange(e.target.value as 'due')}>
      <option value="due">Frist</option>
      <option value="start">Start</option>
    </select>
  );
}

const DATE_UNITS: { value: 'minutes' | 'hours' | 'days' | 'weeks' | 'workdays'; label: string }[] = [
  { value: 'days', label: 'Kalendertage' },
  { value: 'workdays', label: 'Arbeitstage' },
  { value: 'weeks', label: 'Wochen' },
  { value: 'hours', label: 'Stunden (Dauer)' },
  { value: 'minutes', label: 'Minuten (Dauer)' },
];

const withUnit = <T extends { unit: string; holidays?: HolidayRegion | null }>(a: T, unit: string): T =>
  unit === 'workdays' ? { ...a, unit, holidays: a.holidays ?? accountPref<HolidayRegion | null>('holidayRegion', null) } : { ...a, unit, holidays: undefined };

/** Version-1-Bedingungen im neuen Editor als gleichwertige neue Bausteine zeigen */
function upgradePredicate(p: AutomationPredicate): AutomationPredicate {
  if (p.type === 'hasLabel') return { type: 'labels', mode: 'any', labelIds: [p.labelId] };
  if (p.type === 'notLabel') return { type: 'labels', mode: 'none', labelIds: [p.labelId] };
  if (p.type === 'priorityAtLeast') return { type: 'priority', op: 'gte', value: p.value };
  return p;
}
function upgradeGroup(g: ConditionGroup): ConditionGroup {
  return { op: g.op, items: g.items.map((x) => (isGroup(x) ? upgradeGroup(x) : upgradePredicate(x))) };
}

function PredicateEditor({ p, s, onChange }: { p: AutomationPredicate; s: BoardSnapshot; onChange: (p: AutomationPredicate) => void }) {
  const cols = useMemo(() => [...s.columns].sort(byPosition), [s.columns]);
  const t = p.type === 'hasLabel' || p.type === 'notLabel' ? 'labels' : p.type === 'priorityAtLeast' ? 'priority' : p.type;
  return (
    <>
      <select aria-label="Bedingung" className={sel} value={t} onChange={(e) => onChange(defaultPredicate(e.target.value as (typeof PREDICATE_TYPES)[number], s))}>
        {PREDICATE_TYPES.filter((x) => x !== 'field' || s.fields.length || p.type === 'field').map((x) => (
          <option key={x} value={x}>
            {PREDICATE_LABEL[x]}
          </option>
        ))}
      </select>
      {p.type === 'labels' && (
        <>
          <select aria-label="Labels: Art" className={sel} value={p.mode} onChange={(e) => onChange({ ...p, mode: e.target.value as 'any' })}>
            <option value="any">hat eines von</option>
            <option value="all">hat alle</option>
            <option value="none">hat keines von</option>
          </select>
          <ChipPicker label="Labels" options={s.labels.map((l) => ({ id: l.id, name: labelName(l) }))} value={p.labelIds} onChange={(labelIds) => onChange({ ...p, labelIds })} />
        </>
      )}
      {p.type === 'column' && (
        <>
          <select aria-label="Spalte: Art" className={sel} value={p.mode} onChange={(e) => onChange({ ...p, mode: e.target.value as 'in' })}>
            <option value="in">liegt in</option>
            <option value="notIn">liegt nicht in</option>
          </select>
          <ChipPicker label="Spalten" options={cols.map((c) => ({ id: c.id, name: c.title }))} value={p.columnIds} onChange={(columnIds) => onChange({ ...p, columnIds })} />
        </>
      )}
      {p.type === 'done' && (
        <select aria-label="Erledigt" className={sel} value={p.value ? '1' : '0'} onChange={(e) => onChange({ ...p, value: e.target.value === '1' })}>
          <option value="0">Karte ist offen</option>
          <option value="1">Karte ist erledigt</option>
        </select>
      )}
      {p.type === 'blocked' && (
        <select aria-label="Blockiert" className={sel} value={p.value ? '1' : '0'} onChange={(e) => onChange({ ...p, value: e.target.value === '1' })}>
          <option value="1">ist blockiert</option>
          <option value="0">ist nicht blockiert</option>
        </select>
      )}
      {p.type === 'priority' && (
        <>
          <select aria-label="Priorität: Vergleich" className={sel} value={p.op} onChange={(e) => onChange({ ...p, op: e.target.value as 'gte' })}>
            <option value="gte">mindestens</option>
            <option value="lte">höchstens</option>
            <option value="eq">genau</option>
          </select>
          <select aria-label="Priorität" className={sel} value={p.value} onChange={(e) => onChange({ ...p, value: Number(e.target.value) })}>
            {PRIORITIES.map((x) => (
              <option key={x.value} value={x.value}>
                {x.label}
              </option>
            ))}
          </select>
        </>
      )}
      {p.type === 'assignees' && (
        <>
          <select aria-label="Zuständige" className={sel} value={p.mode} onChange={(e) => onChange({ ...p, mode: e.target.value as 'none', userId: e.target.value === 'includes' ? (s.members[0]?.id ?? '') : undefined })}>
            <option value="none">niemand zuständig</option>
            <option value="some">jemand zuständig</option>
            <option value="includes">bestimmte Person zuständig</option>
          </select>
          {p.mode === 'includes' && (
            <select aria-label="Person" className={sel} value={p.userId ?? ''} onChange={(e) => onChange({ ...p, userId: e.target.value })}>
              {s.members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          )}
        </>
      )}
      {p.type === 'dates' && (
        <>
          <select aria-label="Termin" className={sel} value={p.which} onChange={(e) => onChange({ ...p, which: e.target.value as 'due' })}>
            <option value="due">Frist</option>
            <option value="start">Start</option>
          </select>
          <select aria-label="Termin: Zustand" className={sel} value={p.op} onChange={(e) => onChange({ ...p, op: e.target.value as 'set' })}>
            <option value="set">ist gesetzt</option>
            <option value="missing">fehlt</option>
          </select>
        </>
      )}
      {p.type === 'dateWindow' && (
        <>
          <WhichSelect value={p.which} onChange={(which) => onChange({ ...p, which })} />
          <select
            aria-label="Termin: Zeitpunkt"
            className={sel}
            value={p.op}
            onChange={(e) => {
              const op = e.target.value as 'within';
              onChange({ ...p, op, minutes: op === 'within' || op === 'overBy' ? (p.minutes || 1440) : undefined });
            }}
          >
            <option value="reached">ist erreicht</option>
            <option value="notReached">ist noch nicht erreicht</option>
            <option value="within">wird erreicht in höchstens</option>
            <option value="overBy">ist erreicht seit mindestens</option>
          </select>
          {(p.op === 'within' || p.op === 'overBy') && <DurationInput label="Zeitraum" minutes={p.minutes ?? 1440} onChange={(minutes) => onChange({ ...p, minutes })} />}
        </>
      )}
      {p.type === 'effort' && (
        <>
          <select aria-label="Aufwand: Vergleich" className={sel} value={p.op} onChange={(e) => onChange({ ...p, op: e.target.value as 'gte' })}>
            <option value="gte">gebuchte Zeit mindestens</option>
            <option value="lt">gebuchte Zeit unter</option>
          </select>
          {p.measure === 'percent' ? (
            <input
              aria-label="Prozent der Schätzung"
              type="number"
              min={1}
              max={1000}
              className={cx(sel, 'w-20')}
              value={p.value}
              onChange={(e) => onChange({ ...p, value: Math.max(1, Math.min(1000, Math.floor(Number(e.target.value) || 1))) })}
            />
          ) : (
            <DurationInput label="Gebuchte Zeit" minutes={p.value} units={DURATION_UNITS.slice(0, 2)} onChange={(value) => onChange({ ...p, value })} />
          )}
          <select
            aria-label="Aufwand: Maß"
            className={sel}
            value={p.measure}
            onChange={(e) => onChange({ ...p, measure: e.target.value as 'percent', value: e.target.value === 'percent' ? 80 : 120 })}
          >
            <option value="percent">% der Schätzung</option>
            <option value="minutes">Zeit</option>
          </select>
          <label className="flex min-h-11 items-center gap-1.5 text-sm sm:min-h-9">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={p.includeRunning} onChange={(e) => onChange({ ...p, includeRunning: e.target.checked })} />
            laufende Timer mitzählen
          </label>
          {p.measure === 'percent' && <span className="w-full text-xs text-[var(--ink-soft)]">Ohne Schätzung (oder 0) ist das nicht auswertbar und trifft nicht zu.</span>}
        </>
      )}
      {p.type === 'predecessors' && (
        <select aria-label="Vorgänger: Zustand" className={sel} value={p.op} onChange={(e) => onChange({ ...p, op: e.target.value as 'allDone' })}>
          <option value="allDone">alle erledigt</option>
          <option value="open">noch welche offen</option>
          <option value="none">keine vorhanden</option>
        </select>
      )}
      {p.type === 'ready' && (
        <select aria-label="Startbereit" className={sel} value={p.value ? '1' : '0'} onChange={(e) => onChange({ ...p, value: e.target.value === '1' })}>
          <option value="1">ist startbereit</option>
          <option value="0">ist nicht startbereit</option>
        </select>
      )}
      {(p.type === 'dwell' || p.type === 'inactive') && (
        <>
          <select aria-label={p.type === 'dwell' ? 'Zeit in der Spalte: Vergleich' : 'Ohne Bearbeitung: Vergleich'} className={sel} value={p.op} onChange={(e) => onChange({ ...p, op: e.target.value as 'atLeast' })}>
            <option value="atLeast">seit mindestens</option>
            <option value="lessThan">seit weniger als</option>
          </select>
          <DurationInput label={p.type === 'dwell' ? 'Zeit in der Spalte' : 'Zeit ohne Bearbeitung'} minutes={p.minutes} onChange={(minutes) => onChange({ ...p, minutes })} />
          <span className="w-full text-xs text-[var(--ink-soft)]">
            {p.type === 'dwell'
              ? 'Gezählt ab dem letzten Eintritt in die aktuelle Spalte – herausziehen und wieder hineinschieben beginnt neu.'
              : 'Zählt Bearbeitungen durch Personen (Karte, Kommentar, Datei, Zeit). Öffnen und Automationen zählen nicht.'}
          </span>
        </>
      )}
      {p.type === 'estimate' && (
        <select aria-label="Schätzung" className={sel} value={p.op} onChange={(e) => onChange({ ...p, op: e.target.value as 'set' })}>
          <option value="set">ist vorhanden</option>
          <option value="missing">fehlt</option>
        </select>
      )}
      {p.type === 'checklist' && (
        <select aria-label="Checkliste: Zustand" className={sel} value={p.op} onChange={(e) => onChange({ ...p, op: e.target.value as 'complete' })}>
          <option value="complete">ist vollständig abgehakt</option>
          <option value="incomplete">hat noch offene Punkte</option>
          <option value="none">gibt es nicht</option>
        </select>
      )}
      {p.type === 'files' && (
        <>
          <select aria-label="Anzahl" className={sel} value={p.min <= 0 ? 'none' : 'min'} onChange={(e) => onChange({ ...p, min: e.target.value === 'none' ? 0 : 1 })}>
            <option value="min">mindestens</option>
            <option value="none">keine</option>
          </select>
          {p.min > 0 && <input aria-label="Mindestanzahl" type="number" min={1} max={99} className={cx(sel, 'w-20')} value={p.min} onChange={(e) => onChange({ ...p, min: Math.max(1, Math.min(99, Math.floor(Number(e.target.value) || 1))) })} />}
          <select aria-label="Dateiart" className={sel} value={p.kind} onChange={(e) => onChange({ ...p, kind: e.target.value as FileKind })}>
            {FILE_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <label className="flex min-h-11 items-center gap-1.5 text-sm sm:min-h-9">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={p.includeComments} onChange={(e) => onChange({ ...p, includeComments: e.target.checked })} />
            auch aus Kommentaren
          </label>
        </>
      )}
      {p.type === 'actor' && <ActorEditor p={p} s={s} onChange={onChange} />}
      {p.type === 'field' && (
        <>
          <select
            aria-label="Feld"
            className={sel}
            value={p.fieldId}
            onChange={(e) => {
              const f = s.fields.find((x) => x.id === e.target.value);
              onChange({ ...p, fieldId: e.target.value, test: defaultTest(f) });
            }}
          >
            {!s.fields.some((f) => f.id === p.fieldId) && <option value={p.fieldId}>(gelöschtes Feld)</option>}
            {s.fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
          <FieldTestEditor field={s.fields.find((f) => f.id === p.fieldId)} test={p.test} members={s.members} onChange={(test) => onChange({ ...p, test })} />
        </>
      )}
    </>
  );
}

export function GroupEditor({
  g,
  s,
  nested,
  total,
  onChange,
  onRemove,
}: {
  g: ConditionGroup;
  s: BoardSnapshot;
  nested?: boolean;
  total: number;
  onChange: (g: ConditionGroup) => void;
  onRemove?: () => void;
}) {
  const set = (i: number, x: AutomationPredicate | ConditionGroup) => onChange({ ...g, items: g.items.map((y, j) => (j === i ? x : y)) });
  const remove = (i: number) => onChange({ ...g, items: g.items.filter((_, j) => j !== i) });
  const full = total >= AUTOMATION_MAX_PREDICATES;
  return (
    <div className={cx('flex flex-col gap-2', nested && 'rounded-lg border-l-4 border-violet-300 bg-black/[0.02] py-2 pr-2 pl-3 dark:border-violet-700 dark:bg-white/[0.03]')}>
      {(g.items.length > 1 || nested) && (
        <div className="flex items-start gap-2 text-sm">
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
            <select aria-label={nested ? 'Verknüpfung der Gruppe' : 'Verknüpfung'} className={sel} value={g.op} onChange={(e) => onChange({ ...g, op: e.target.value as 'all' })}>
              <option value="all">Alle Bedingungen</option>
              <option value="any">Mindestens eine Bedingung</option>
            </select>
            <span>{nested ? 'dieser Gruppe müssen zutreffen' : 'müssen zutreffen'}</span>
          </span>
          {onRemove && (
            <button type="button" aria-label="Gruppe entfernen" className={cx(iconBtn, 'hover:text-rose-600')} onClick={onRemove}>
              <X size={16} />
            </button>
          )}
        </div>
      )}
      {g.items.map((x, i) =>
        isGroup(x) ? (
          <GroupEditor key={i} g={x} s={s} nested total={total} onChange={(ng) => set(i, ng)} onRemove={() => remove(i)} />
        ) : (
          <div key={i} className="flex flex-wrap items-center gap-2">
            {i > 0 && <span className="w-full text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase sm:w-auto">{g.op === 'all' ? 'und' : 'oder'}</span>}
            <PredicateEditor p={x} s={s} onChange={(p) => set(i, p)} />
            <button type="button" aria-label="Bedingung entfernen" className={cx(iconBtn, 'hover:text-rose-600')} onClick={() => remove(i)}>
              <X size={16} />
            </button>
          </div>
        ),
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={full}
          className="inline-flex min-h-11 items-center gap-1 text-sm text-accent-fg disabled:opacity-50 sm:min-h-9"
          onClick={() => onChange({ ...g, items: [...g.items, defaultPredicate(s.labels.length ? 'labels' : 'column', s)] })}
        >
          <Plus size={15} /> Bedingung hinzufügen
        </button>
        {!nested && (
          <button
            type="button"
            disabled={full}
            className="inline-flex min-h-11 items-center gap-1 text-sm text-accent-fg disabled:opacity-50 sm:min-h-9"
            onClick={() => onChange({ ...g, items: [...g.items, { op: g.op === 'all' ? 'any' : 'all', items: [defaultPredicate('column', s)] }] })}
          >
            <Plus size={15} /> Gruppe hinzufügen
          </button>
        )}
      </div>
    </div>
  );
}

function defaultAction(type: AutomationAction['type'], s: BoardSnapshot): AutomationAction {
  const cols = [...s.columns].sort(byPosition);
  const f = s.fields[0];
  switch (type) {
    case 'move':
      return { type, columnId: cols.at(-1)?.id ?? '', placement: 'top' };
    case 'assign':
      return { type, userId: 'actor' };
    case 'addLabel':
    case 'removeLabel':
      return { type, labelId: s.labels[0]?.id ?? '' };
    case 'labels':
      return { type, add: [], remove: [] };
    case 'setField': {
      const g = s.fields.find((x) => x.type !== 'approval');
      return { type, fieldId: g?.id ?? '', value: g?.type === 'checkbox' ? true : '' };
    }
    case 'clearField':
      return { type, fieldId: f?.id ?? '' };
    case 'fieldOptions':
      return { type, fieldId: s.fields.find((x) => x.type === 'multiselect')?.id ?? '', add: [], remove: [] };
    case 'setDue':
      return { type, days: 7 };
    case 'setDate':
      return { type, which: 'due', base: 'start', amount: 3, unit: 'days', time: '17:00', onlyIfEmpty: true };
    case 'shiftDate':
      return { type, which: 'due', amount: 1, unit: 'days' };
    case 'clearDate':
      return { type, which: 'due' };
    case 'setEstimate':
      return { type, minutes: 60 };
    case 'setPriority':
      return { type, value: 3 };
    case 'addChecklist':
      return { type, items: [''] };
    case 'notify':
      return { type, text: '', recipients: { assignees: true, users: [], groups: [] } };
    case 'calcField':
      return { type, fieldId: s.fields.find((x) => x.type === 'number' || x.type === 'currency')?.id ?? '', op: 'add', value: 1, fromFieldId: null };
    case 'createCard':
      return { type, boardId: null, columnId: cols[0]?.id ?? '', title: 'Folgekarte: {titel}', copy: { fields: true, labels: true, description: false, assignees: false } };
    default:
      return { type } as AutomationAction;
  }
}

function FieldValueInput({ field, value, onChange, members }: { field: CustomField | undefined; value: unknown; onChange: (v: string | number | boolean | string[]) => void; members: BoardSnapshot['members'] }) {
  if (!field) return null;
  const kind = fieldTestKind(field.type);
  if (kind === 'checkbox')
    return (
      <select aria-label="Wert" className={sel} value={value ? '1' : '0'} onChange={(e) => onChange(e.target.value === '1')}>
        <option value="1">angehakt</option>
      </select>
    );
  if (kind === 'select')
    return (
      <select aria-label="Wert" className={sel} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
        <option value="">Auswahl …</option>
        {field.options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    );
  if (kind === 'multiselect')
    return <ChipPicker label="Werte" options={field.options.map((o) => ({ id: o.id, name: o.label }))} value={Array.isArray(value) ? (value as string[]) : []} onChange={(v) => onChange(v)} />;
  if (kind === 'person')
    return (
      <select aria-label="Person" className={sel} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
        <option value="">Person …</option>
        {members.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>
    );
  return (
    <input
      aria-label="Wert"
      className={cx(sel, kind === 'number' ? 'w-32' : 'min-w-0 flex-1')}
      type={kind === 'number' ? 'number' : kind === 'date' ? 'date' : 'text'}
      step="any"
      value={value === null || value === undefined ? '' : String(value)}
      onChange={(e) => onChange(kind === 'number' ? Number(e.target.value) : e.target.value)}
    />
  );
}

function TriggerEditor({
  t,
  s,
  onChange,
  once,
  onOnce,
}: {
  t: AutomationTrigger;
  s: BoardSnapshot;
  onChange: (t: AutomationTrigger) => void;
  once: boolean;
  onOnce: (v: boolean) => void;
}) {
  const cols = useMemo(() => [...s.columns].sort(byPosition), [s.columns]);
  const make = (type: AutomationTrigger['type']): AutomationTrigger => {
    switch (type) {
      case 'moved':
      case 'left':
        return { type, columnId: cols.at(-1)?.id ?? '' };
      case 'created':
        return { type, columnId: null };
      case 'labelAdded':
      case 'labelRemoved':
        return { type, labelIds: [] };
      case 'fieldChanged':
        return { type, fieldId: s.fields[0]?.id ?? '' };
      case 'fileAdded':
      case 'fileRemoved':
        return { type, kind: 'any', includeComments: false };
      case 'dateChanged':
        return { type, which: 'start', change: 'set' };
      case 'dateReached':
        return { type, which: 'due', offset: { mode: 'calendar', days: -1, time: '09:00' }, repeat: null, catchUpMinutes: 0 };
      case 'effortChanged':
        return { type, what: 'spent' };
      case 'manual':
        return { type, label: 'Zur Freigabe vorbereiten' };
      default:
        return { type } as AutomationTrigger;
    }
  };
  const field = t.type === 'fieldChanged' ? s.fields.find((f) => f.id === t.fieldId) : undefined;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="Auslöser" className={sel} value={t.type} onChange={(e) => onChange(make(e.target.value as AutomationTrigger['type']))}>
          {TRIGGER_GROUPS.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.types
                .filter((x) => x !== 'fieldChanged' || s.fields.length || t.type === x)
                .map((x) => (
                  <option key={x} value={x}>
                    {TRIGGER_LABEL[x]}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
        {(t.type === 'moved' || t.type === 'left' || t.type === 'created') && (
          <select
            aria-label="Spalte"
            className={sel}
            value={t.columnId ?? ''}
            onChange={(e) => onChange({ ...t, columnId: e.target.value || (t.type === 'created' ? null : cols[0]!.id) } as AutomationTrigger)}
          >
            {t.type === 'created' && <option value="">in beliebiger Spalte</option>}
            {cols.map((c) => (
              <option key={c.id} value={c.id}>
                {t.type === 'moved' ? 'nach' : t.type === 'left' ? 'aus' : 'in'} „{c.title}“
              </option>
            ))}
          </select>
        )}
        {(t.type === 'fileAdded' || t.type === 'fileRemoved') && (
          <>
            <select aria-label="Dateiart" className={sel} value={t.kind} onChange={(e) => onChange({ ...t, kind: e.target.value as FileKind })}>
              {FILE_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
            <label className="flex min-h-11 items-center gap-1.5 text-sm sm:min-h-9">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={t.includeComments} onChange={(e) => onChange({ ...t, includeComments: e.target.checked })} />
              auch aus Kommentaren
            </label>
          </>
        )}
        {t.type === 'dateChanged' && (
          <>
            <WhichSelect value={t.which} onChange={(which) => onChange({ ...t, which })} />
            <select aria-label="Änderung" className={sel} value={t.change} onChange={(e) => onChange({ ...t, change: e.target.value as 'set' })}>
              <option value="set">wird gesetzt (war leer)</option>
              <option value="changed">wird geändert</option>
              <option value="removed">wird entfernt</option>
              <option value="any">wird gesetzt, geändert oder entfernt</option>
            </select>
          </>
        )}
        {t.type === 'effortChanged' && (
          <select aria-label="Aufwand" className={sel} value={t.what} onChange={(e) => onChange({ ...t, what: e.target.value as 'spent' })}>
            <option value="spent">Zeit gebucht oder korrigiert</option>
            <option value="estimate">Schätzung geändert</option>
          </select>
        )}
        {t.type === 'fieldChanged' && (
          <select aria-label="Feld" className={sel} value={t.fieldId} onChange={(e) => onChange({ type: 'fieldChanged', fieldId: e.target.value })}>
            {!field && <option value={t.fieldId}>(gelöschtes Feld)</option>}
            {s.fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        )}
      </div>
      {t.type === 'dateReached' && <DateReachedEditor t={t} dateFields={s.fields.filter((f) => f.type === 'date')} onChange={onChange} />}
      {t.type === 'manual' && (
        <div className="flex flex-col gap-1.5">
          <label className="flex flex-col gap-1 text-sm">
            Beschriftung des Knopfs
            <input className={sel} maxLength={40} value={t.label} onChange={(e) => onChange({ ...t, label: e.target.value })} required />
          </label>
          <p className="text-xs text-[var(--ink-soft)]">
            Der Knopf erscheint in den Kartendetails unter „Abläufe“ für alle, die die Karte bearbeiten dürfen. Sind die Bedingungen nicht erfüllt, passiert nichts – die
            Meldung nennt, was fehlt.
          </p>
        </div>
      )}
      {(t.type === 'predecessorsDone' || t.type === 'ready') && (
        <p className="text-xs text-[var(--ink-soft)]">
          {t.type === 'predecessorsDone'
            ? 'Läuft, sobald der letzte erforderliche Vorgänger erledigt wird. Ein archivierter, nicht erledigter Vorgänger bleibt offen; Karten ohne Vorgänger lösen nie aus.'
            : 'Startbereit heißt: alle erforderlichen Vorgänger erledigt, die Karte offen und nicht blockiert, der Start erreicht (ganztägig ab Tagesbeginn) – wie bei den Übergaben. Karten ohne Vorgänger werden nie startbereit.'}
        </p>
      )}
      {t.type === 'stateMet' && (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-[var(--ink-soft)]">
            Läuft, sobald eine Karte alle Bedingungen erfüllt – egal, in welcher Reihenfolge das passiert. Solange der Zustand anhält, läuft die Regel nicht
            erneut; fällt er weg und tritt wieder ein, läuft sie wieder. Karten, die beim Speichern schon passen, bleiben unberührt – dafür gibt es „Auf
            bestehende Karten anwenden“.
          </p>
          <label className="flex min-h-11 items-center gap-1.5 text-sm sm:min-h-9">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={once} onChange={(e) => onOnce(e.target.checked)} />
            Nur einmal je Karte
          </label>
        </div>
      )}
      {(t.type === 'labelAdded' || t.type === 'labelRemoved') && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-[var(--ink-soft)]">{t.labelIds.length ? 'Nur bei diesen Labels:' : 'Bei jedem Label – oder nur bei bestimmten:'}</span>
          <ChipPicker label="Labels" options={s.labels.map((l) => ({ id: l.id, name: labelName(l) }))} value={t.labelIds} onChange={(labelIds) => onChange({ ...t, labelIds })} />
        </div>
      )}
      {t.type === 'fieldChanged' && field && (
        <div className="flex flex-col gap-2">
          {(['from', 'to'] as const).map((k) => (
            <div key={k} className="flex flex-wrap items-center gap-2">
              <label className="flex min-h-11 items-center gap-1.5 text-sm sm:min-h-9">
                <input
                  type="checkbox"
                  className="size-5 accent-[var(--color-accent)]"
                  checked={!!t[k]}
                  onChange={(e) => onChange({ ...t, [k]: e.target.checked ? defaultTest(field) : null })}
                />
                {k === 'from' ? 'nur wenn der Wert vorher' : 'nur wenn der neue Wert'}
              </label>
              {t[k] && <FieldTestEditor field={field} test={t[k]!} members={s.members} onChange={(x) => onChange({ ...t, [k]: x })} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** 0.63: Empfänger einer Nachricht – Rollen, Personen, Teamgruppen (zum Ausführungszeitpunkt aufgelöst) */
function RecipientsEditor({ a, s, onChange }: { a: Extract<AutomationAction, { type: 'notify' }>; s: BoardSnapshot; onChange: (a: AutomationAction) => void }) {
  const r = a.recipients ?? { assignees: true, users: [], groups: [] };
  const set = (x: Partial<typeof r>) => onChange({ ...a, recipients: { ...r, ...x } });
  const { data: targets } = useReminderTargets(s.board.id, true);
  const roles: [keyof typeof r & ('assignees' | 'owner' | 'watchers'), string][] = [
    ['assignees', 'Zuständige'],
    ['owner', 'Hauptverantwortliche'],
    ['watchers', 'Beobachtende'],
  ];
  return (
    <fieldset className="flex min-w-0 flex-col gap-1.5">
      <legend className="text-xs text-[var(--ink-soft)]">An wen?</legend>
      <div className="flex flex-wrap gap-x-3">
        {roles.map(([k, label]) => (
          <label key={k} className="flex min-h-11 items-center gap-1.5 text-sm sm:min-h-9">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={!!r[k]} onChange={(e) => set({ [k]: e.target.checked })} />
            {label}
          </label>
        ))}
      </div>
      <ChipPicker label="Personen" options={s.members.map((m) => ({ id: m.id, name: m.name }))} value={r.users} onChange={(users) => set({ users })} />
      {!!targets?.groups.length && <ChipPicker label="Gruppen" options={targets.groups.map((g) => ({ id: g.id, name: g.name }))} value={r.groups} onChange={(groups) => set({ groups })} />}
      <span className="text-xs text-[var(--ink-soft)]">Jede Person bekommt die Nachricht nur einmal; nur wer Zugriff auf das Board hat. Gruppen gelten mit ihren aktuellen Mitgliedern.</span>
    </fieldset>
  );
}

type OffsetKind = 'at' | 'before' | 'after' | 'dayBefore' | 'sameDay' | 'dayAfter' | 'wdBefore' | 'wdAfter';

/** 0.61: „Start/Frist erreicht“ – Abstand, Wiederholung, Verhalten nach einem Ausfall */
function DateReachedEditor({ t, dateFields, onChange }: { t: Extract<AutomationTrigger, { type: 'dateReached' }>; dateFields: CustomField[]; onChange: (t: AutomationTrigger) => void }) {
  const o = t.offset;
  const kind: OffsetKind =
    o.mode === 'at' || (o.mode === 'duration' && o.minutes === 0)
      ? 'at'
      : o.mode === 'duration'
        ? o.minutes < 0
          ? 'before'
          : 'after'
        : o.mode === 'workdays'
          ? o.days < 0
            ? 'wdBefore'
            : 'wdAfter'
          : o.days < 0
            ? 'dayBefore'
            : o.days > 0
              ? 'dayAfter'
              : 'sameDay';
  const dayCount = (o.mode === 'calendar' || o.mode === 'workdays') && o.days ? Math.abs(o.days) : 1;
  const timeOf = o.mode === 'calendar' || o.mode === 'workdays' ? o.time : '09:00';
  const setKind = (k: OffsetKind) =>
    onChange({
      ...t,
      offset:
        k === 'at'
          ? { mode: 'at' }
          : k === 'before' || k === 'after'
            ? { mode: 'duration', minutes: (k === 'before' ? -1 : 1) * (o.mode === 'duration' && o.minutes ? Math.abs(o.minutes) : 60) }
            : k === 'wdBefore' || k === 'wdAfter'
              ? {
                  mode: 'workdays',
                  days: (k === 'wdBefore' ? -1 : 1) * dayCount,
                  time: timeOf,
                  holidays: o.mode === 'workdays' ? (o.holidays ?? null) : accountPref<HolidayRegion | null>('holidayRegion', null),
                }
              : { mode: 'calendar', days: k === 'sameDay' ? 0 : (k === 'dayBefore' ? -1 : 1) * dayCount, time: timeOf },
    });
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {dateFields.length || t.fieldId ? (
          <select
            aria-label="Termin"
            className={sel}
            value={t.fieldId ? `f:${t.fieldId}` : t.which}
            onChange={(e) => {
              const v = e.target.value;
              onChange(v.startsWith('f:') ? { ...t, which: 'start', fieldId: v.slice(2) } : { ...t, which: v as 'due', fieldId: null });
            }}
          >
            <option value="due">Frist</option>
            <option value="start">Start</option>
            {t.fieldId && !dateFields.some((f) => f.id === t.fieldId) && <option value={`f:${t.fieldId}`}>(gelöschtes Feld)</option>}
            {dateFields.map((f) => (
              <option key={f.id} value={`f:${f.id}`}>
                „{f.name}“
              </option>
            ))}
          </select>
        ) : (
          <WhichSelect value={t.which} onChange={(which) => onChange({ ...t, which })} />
        )}
        <select aria-label="Zeitpunkt" className={sel} value={kind} onChange={(e) => setKind(e.target.value as OffsetKind)}>
          <option value="at">genau dann</option>
          <option value="before">eine Zeitspanne davor</option>
          <option value="after">eine Zeitspanne danach</option>
          <option value="dayBefore">Kalendertage davor, zu einer Uhrzeit</option>
          <option value="sameDay">am selben Tag, zu einer Uhrzeit</option>
          <option value="dayAfter">Kalendertage danach, zu einer Uhrzeit</option>
          <option value="wdBefore">Arbeitstage davor, zu einer Uhrzeit</option>
          <option value="wdAfter">Arbeitstage danach, zu einer Uhrzeit</option>
        </select>
        {o.mode === 'duration' && o.minutes !== 0 && (
          <DurationInput label="Zeitspanne" minutes={Math.abs(o.minutes)} onChange={(m) => onChange({ ...t, offset: { mode: 'duration', minutes: Math.sign(o.minutes) * m } })} />
        )}
        {o.mode === 'calendar' && (
          <>
            {o.days !== 0 && (
              <input
                aria-label="Kalendertage"
                type="number"
                min={1}
                max={60}
                className={cx(sel, 'w-20')}
                value={Math.abs(o.days)}
                onChange={(e) => onChange({ ...t, offset: { ...o, days: Math.sign(o.days) * Math.max(1, Math.min(60, Math.floor(Number(e.target.value) || 1))) } })}
              />
            )}
            <span className="text-sm">um</span>
            <input aria-label="Uhrzeit" type="time" className={sel} value={o.time} onChange={(e) => onChange({ ...t, offset: { ...o, time: e.target.value || '09:00' } })} />
          </>
        )}
        {o.mode === 'workdays' && (
          <>
            <input
              aria-label="Arbeitstage"
              type="number"
              min={1}
              max={60}
              className={cx(sel, 'w-20')}
              value={Math.abs(o.days)}
              onChange={(e) => onChange({ ...t, offset: { ...o, days: Math.sign(o.days || -1) * Math.max(1, Math.min(60, Math.floor(Number(e.target.value) || 1))) } })}
            />
            <span className="text-sm">um</span>
            <input aria-label="Uhrzeit" type="time" className={sel} value={o.time} onChange={(e) => onChange({ ...t, offset: { ...o, time: e.target.value || '09:00' } })} />
            <HolidaySelect value={o.holidays} onChange={(holidays) => onChange({ ...t, offset: { ...o, holidays } })} />
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-h-11 items-center gap-1.5 text-sm sm:min-h-9">
          <input
            type="checkbox"
            className="size-5 accent-[var(--color-accent)]"
            checked={!!t.repeat}
            onChange={(e) => onChange({ ...t, repeat: e.target.checked ? { every: 1, unit: 'days', max: 3 } : null })}
          />
          danach wiederholen
        </label>
        {t.repeat && (
          <>
            <span className="text-sm">alle</span>
            <input
              aria-label="Wiederholen alle"
              type="number"
              min={1}
              max={30}
              className={cx(sel, 'w-16')}
              value={t.repeat.every}
              onChange={(e) => onChange({ ...t, repeat: { ...t.repeat!, every: Math.max(1, Math.min(30, Math.floor(Number(e.target.value) || 1))) } })}
            />
            <select aria-label="Wiederholen: Einheit" className={sel} value={t.repeat.unit} onChange={(e) => onChange({ ...t, repeat: { ...t.repeat!, unit: e.target.value as 'days' } })}>
              <option value="hours">Stunden</option>
              <option value="days">Kalendertage</option>
            </select>
            <span className="text-sm">höchstens</span>
            <input
              aria-label="Höchstens wie oft"
              type="number"
              min={1}
              max={30}
              className={cx(sel, 'w-16')}
              value={t.repeat.max}
              onChange={(e) => onChange({ ...t, repeat: { ...t.repeat!, max: Math.max(1, Math.min(30, Math.floor(Number(e.target.value) || 1))) } })}
            />
            <span className="text-sm">-mal</span>
          </>
        )}
      </div>
      <label className="flex flex-wrap items-center gap-2 text-sm">
        Nach einem Serverausfall
        <select aria-label="Nach einem Serverausfall" className={sel} value={t.catchUpMinutes ?? 0} onChange={(e) => onChange({ ...t, catchUpMinutes: Number(e.target.value) })}>
          <option value={0}>nicht nachholen</option>
          <option value={60}>bis 1 Stunde später nachholen</option>
          <option value={360}>bis 6 Stunden später nachholen</option>
          <option value={1440}>bis 24 Stunden später nachholen</option>
        </select>
      </label>
      <p className="text-xs text-[var(--ink-soft)]">
        Gilt für offene Karten mit diesem Termin. Wird der Termin verschoben, gilt der neue Zeitpunkt; ein ganztägiger Start zählt ab 09:00 Uhr, eine
        ganztägige Frist bis Tagesende. Kalendertage behalten die Uhrzeit auch bei Sommer-/Winterzeit.
      </p>
    </div>
  );
}

/** 0.61: Start, Frist oder Datumsfeld relativ setzen */
function SetDateEditor({ a, s, onChange }: { a: Extract<AutomationAction, { type: 'setDate' }>; s: BoardSnapshot; onChange: (a: AutomationAction) => void }) {
  const dateFields = s.fields.filter((f) => f.type === 'date');
  const target = a.which === 'field' ? `field:${a.fieldId ?? ''}` : a.which;
  const base = a.base === 'field' ? `field:${a.baseFieldId ?? ''}` : a.base;
  const calendar = a.unit === 'days' || a.unit === 'weeks' || a.unit === 'workdays';
  const timeMode = a.time === 'base' || a.time === 'allDay' ? a.time : 'fixed';
  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Ziel"
          className={sel}
          value={target}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v.startsWith('field:') ? { ...a, which: 'field', fieldId: v.slice(6), time: 'allDay' } : { ...a, which: v as 'due', fieldId: undefined });
          }}
        >
          <option value="due">Frist</option>
          <option value="start">Start</option>
          {dateFields.map((f) => (
            <option key={f.id} value={`field:${f.id}`}>
              {f.name}
            </option>
          ))}
        </select>
        <span className="text-sm">auf</span>
        <input
          aria-label="Abstand"
          type="number"
          min={0}
          max={9999}
          className={cx(sel, 'w-20')}
          value={Math.abs(a.amount)}
          onChange={(e) => onChange({ ...a, amount: (a.amount < 0 ? -1 : 1) * Math.max(0, Math.min(9999, Math.floor(Number(e.target.value) || 0))) })}
        />
        <select aria-label="Einheit" className={sel} value={a.unit} onChange={(e) => onChange(withUnit(a, e.target.value as 'days'))}>
          {DATE_UNITS.map((u) => (
            <option key={u.value} value={u.value}>
              {u.label}
            </option>
          ))}
        </select>
        <select aria-label="Richtung" className={sel} value={a.amount < 0 ? '-1' : '1'} onChange={(e) => onChange({ ...a, amount: Math.abs(a.amount) * Number(e.target.value) })}>
          <option value="1">nach</option>
          <option value="-1">vor</option>
        </select>
        {a.unit === 'workdays' && <HolidaySelect value={a.holidays} onChange={(holidays) => onChange({ ...a, holidays })} />}
        <select
          aria-label="Bezug"
          className={sel}
          value={base}
          onChange={(e) => {
            const v = e.target.value;
            onChange(
              v.startsWith('field:')
                ? { ...a, base: 'field', baseFieldId: v.slice(6) }
                : v === 'fixed'
                  ? { ...a, base: 'fixed', baseDate: a.baseDate ?? new Date().toLocaleDateString('sv-SE'), baseTime: null }
                  : { ...a, base: v as 'now', baseFieldId: undefined },
            );
          }}
        >
          <option value="now">jetzt (Ausführung)</option>
          <option value="event">dem Zeitpunkt des Auslösers</option>
          <option value="start">dem Start</option>
          <option value="due">der Frist</option>
          {dateFields.map((f) => (
            <option key={f.id} value={`field:${f.id}`}>
              „{f.name}“
            </option>
          ))}
          <option value="fixed">einem festen Datum</option>
        </select>
        {a.base === 'fixed' && (
          <>
            <input aria-label="Festes Datum" type="date" className={sel} value={a.baseDate ?? ''} onChange={(e) => onChange({ ...a, baseDate: e.target.value })} />
            <input aria-label="Feste Uhrzeit (optional)" type="time" className={sel} value={a.baseTime ?? ''} onChange={(e) => onChange({ ...a, baseTime: e.target.value || null })} />
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {a.which !== 'field' && calendar && (
          <>
            <select
              aria-label="Uhrzeit des Ergebnisses"
              className={sel}
              value={timeMode}
              onChange={(e) => onChange({ ...a, time: e.target.value === 'fixed' ? '17:00' : e.target.value })}
            >
              <option value="allDay">ganztägig</option>
              <option value="fixed">zur Uhrzeit …</option>
              <option value="base">Uhrzeit wie der Bezug</option>
            </select>
            {timeMode === 'fixed' && <input aria-label="Uhrzeit" type="time" className={sel} value={a.time} onChange={(e) => onChange({ ...a, time: e.target.value || '17:00' })} />}
          </>
        )}
        <label className="flex min-h-11 items-center gap-1.5 text-sm sm:min-h-9">
          <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={a.onlyIfEmpty} onChange={(e) => onChange({ ...a, onlyIfEmpty: e.target.checked })} />
          nur wenn leer
        </label>
      </div>
      <span className="text-xs text-[var(--ink-soft)]">
        {calendar ? 'Kalendertage behalten die Uhrzeit, auch über die Zeitumstellung.' : 'Stunden und Minuten sind eine genaue Zeitspanne.'} Fehlt der Bezug, wartet die Regel
        und setzt nichts. Läge der Start danach nach der Frist, ändert die Regel nichts.
      </span>
    </div>
  );
}

function ActionEditor({ a, s, n, onChange }: { a: AutomationAction; s: BoardSnapshot; n: number; onChange: (a: AutomationAction) => void }) {
  const cols = useMemo(() => [...s.columns].sort(byPosition), [s.columns]);
  const types = ACTION_TYPES_EDITOR.includes(a.type) ? ACTION_TYPES_EDITOR : [a.type, ...ACTION_TYPES_EDITOR];
  const multi = s.fields.filter((f) => f.type === 'multiselect');
  const nums = s.fields.filter((f) => f.type === 'number' || f.type === 'currency');
  return (
    <>
      <select aria-label={`Aktion ${n}`} className={sel} value={a.type} onChange={(e) => onChange(defaultAction(e.target.value as AutomationAction['type'], s))}>
        {types
          .filter((t) => (t !== 'setField' && t !== 'clearField') || s.fields.length || t === a.type)
          .filter((t) => t !== 'fieldOptions' || multi.length || t === a.type)
          .filter((t) => t !== 'calcField' || nums.length || t === a.type)
          .map((t) => (
            <option key={t} value={t}>
              {ACTION_LABEL[t]}
            </option>
          ))}
      </select>
      {a.type === 'calcField' && (
        <>
          <select aria-label="Rechenart" className={sel} value={a.op} onChange={(e) => onChange({ ...a, op: e.target.value as 'add' })}>
            <option value="add">addieren zu</option>
            <option value="sub">abziehen von</option>
            <option value="mul">malnehmen mit (Feld ×)</option>
            <option value="set">setzen auf</option>
          </select>
          <select aria-label="Zahlenfeld" className={sel} value={a.fieldId} onChange={(e) => onChange({ ...a, fieldId: e.target.value })}>
            {!nums.some((f) => f.id === a.fieldId) && <option value={a.fieldId}>(gelöschtes Feld)</option>}
            {nums.map((f) => (
              <option key={f.id} value={f.id}>
                „{f.name}“
              </option>
            ))}
          </select>
          <span className="text-sm">Wert:</span>
          <select
            aria-label="Wert aus"
            className={sel}
            value={a.fromFieldId ? `f:${a.fromFieldId}` : 'n'}
            onChange={(e) => onChange(e.target.value === 'n' ? { ...a, fromFieldId: null, value: a.value ?? 1 } : { ...a, fromFieldId: e.target.value.slice(2), value: null })}
          >
            <option value="n">feste Zahl</option>
            {nums.map((f) => (
              <option key={f.id} value={`f:${f.id}`}>
                aus „{f.name}“
              </option>
            ))}
          </select>
          {!a.fromFieldId && (
            <DecimalInput aria-label="Zahl" className={cx(sel, 'w-24')} value={a.value} min={-1e12} max={1e12} onChange={(value) => onChange({ ...a, value })} />
          )}
        </>
      )}
      {a.type === 'createCard' && <CreateCardEditor a={a} s={s} onChange={onChange} />}
      {a.type === 'move' && (
        <>
          <select aria-label="Zielspalte" className={sel} value={a.columnId} onChange={(e) => onChange({ ...a, columnId: e.target.value })}>
            {cols.map((c) => (
              <option key={c.id} value={c.id}>
                „{c.title}“
              </option>
            ))}
          </select>
          <select aria-label="Position" className={sel} value={a.placement} onChange={(e) => onChange({ ...a, placement: e.target.value as 'top' | 'bottom' })}>
            <option value="top">oben</option>
            <option value="bottom">unten</option>
          </select>
        </>
      )}
      {a.type === 'assign' && (
        <select aria-label="Person" className={sel} value={a.userId} onChange={(e) => onChange({ ...a, userId: e.target.value })}>
          <option value="actor">die auslösende Person</option>
          {s.members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      )}
      {(a.type === 'addLabel' || a.type === 'removeLabel') && (
        <select aria-label="Label" className={sel} value={a.labelId} onChange={(e) => onChange({ ...a, labelId: e.target.value })}>
          {s.labels.map((l) => (
            <option key={l.id} value={l.id}>
              {labelName(l)}
            </option>
          ))}
        </select>
      )}
      {a.type === 'labels' && (
        <div className="flex w-full flex-col gap-1.5">
          <span className="text-xs text-[var(--ink-soft)]">Hinzufügen</span>
          <ChipPicker label="Labels hinzufügen" options={s.labels.filter((l) => !a.remove.includes(l.id)).map((l) => ({ id: l.id, name: labelName(l) }))} value={a.add} onChange={(add) => onChange({ ...a, add })} />
          <span className="text-xs text-[var(--ink-soft)]">Entfernen</span>
          <ChipPicker label="Labels entfernen" options={s.labels.filter((l) => !a.add.includes(l.id)).map((l) => ({ id: l.id, name: labelName(l) }))} value={a.remove} onChange={(remove) => onChange({ ...a, remove })} />
          <span className="text-xs text-[var(--ink-soft)]">Andere Labels der Karte bleiben unverändert.</span>
        </div>
      )}
      {(a.type === 'setField' || a.type === 'clearField') && (
        <select
          aria-label="Feld"
          className={sel}
          value={a.fieldId}
          onChange={(e) => {
            const f = s.fields.find((x) => x.id === e.target.value);
            onChange(a.type === 'setField' ? { ...a, fieldId: e.target.value, value: f?.type === 'checkbox' ? true : f?.type === 'multiselect' ? [] : '' } : { ...a, fieldId: e.target.value });
          }}
        >
          {!s.fields.some((f) => f.id === a.fieldId) && <option value={a.fieldId}>(gelöschtes Feld)</option>}
          {s.fields
            .filter((f) => a.type === 'clearField' || f.type !== 'approval' || f.id === a.fieldId)
            .map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
                {f.type === 'approval' ? ' (zurücksetzen)' : ''}
              </option>
            ))}
        </select>
      )}
      {a.type === 'setField' && (
        <>
          <span className="text-sm">auf</span>
          <FieldValueInput field={s.fields.find((f) => f.id === a.fieldId)} value={a.value} members={s.members} onChange={(value) => onChange({ ...a, value })} />
        </>
      )}
      {a.type === 'fieldOptions' && (
        <div className="flex w-full flex-col gap-1.5">
          <select aria-label="Feld" className={sel} value={a.fieldId} onChange={(e) => onChange({ ...a, fieldId: e.target.value, add: [], remove: [] })}>
            {!multi.some((f) => f.id === a.fieldId) && <option value={a.fieldId}>(gelöschtes Feld)</option>}
            {multi.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
          {(() => {
            const f = multi.find((x) => x.id === a.fieldId);
            if (!f) return null;
            return (
              <>
                <span className="text-xs text-[var(--ink-soft)]">Ergänzen</span>
                <ChipPicker label="Werte ergänzen" options={f.options.filter((o) => !a.remove.includes(o.id)).map((o) => ({ id: o.id, name: o.label }))} value={a.add} onChange={(add) => onChange({ ...a, add })} />
                <span className="text-xs text-[var(--ink-soft)]">Entfernen</span>
                <ChipPicker label="Werte entfernen" options={f.options.filter((o) => !a.add.includes(o.id)).map((o) => ({ id: o.id, name: o.label }))} value={a.remove} onChange={(remove) => onChange({ ...a, remove })} />
              </>
            );
          })()}
        </div>
      )}
      {a.type === 'setDue' && (
        <label className="flex items-center gap-1.5 text-sm">
          in
          <input
            type="number"
            min={0}
            max={365}
            aria-label="Tage"
            className={cx(sel, 'w-20')}
            value={a.days}
            onChange={(e) => onChange({ ...a, days: Math.max(0, Math.min(365, Math.floor(Number(e.target.value) || 0))) })}
          />
          Tagen
        </label>
      )}
      {a.type === 'setDate' && <SetDateEditor a={a} s={s} onChange={onChange} />}
      {a.type === 'shiftDate' && (
        <>
          <WhichSelect value={a.which} onChange={(which) => onChange({ ...a, which })} />
          <span className="text-sm">um</span>
          <input
            aria-label="Abstand"
            type="number"
            min={1}
            max={999}
            className={cx(sel, 'w-20')}
            value={Math.abs(a.amount)}
            onChange={(e) => onChange({ ...a, amount: Math.sign(a.amount || 1) * Math.max(1, Math.min(999, Math.floor(Number(e.target.value) || 1))) })}
          />
          <select aria-label="Einheit" className={sel} value={a.unit} onChange={(e) => onChange(withUnit(a, e.target.value as 'days'))}>
            {DATE_UNITS.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
          <select aria-label="Richtung" className={sel} value={a.amount < 0 ? '-1' : '1'} onChange={(e) => onChange({ ...a, amount: Math.abs(a.amount) * Number(e.target.value) })}>
            <option value="1">nach hinten (später)</option>
            <option value="-1">nach vorne (früher)</option>
          </select>
          {a.unit === 'workdays' && <HolidaySelect value={a.holidays} onChange={(holidays) => onChange({ ...a, holidays })} />}
        </>
      )}
      {a.type === 'clearDate' && <WhichSelect value={a.which} onChange={(which) => onChange({ ...a, which })} />}
      {a.type === 'setEstimate' && (
        <>
          <select aria-label="Schätzung" className={sel} value={a.minutes === null ? 'clear' : 'set'} onChange={(e) => onChange({ ...a, minutes: e.target.value === 'clear' ? null : 60 })}>
            <option value="set">auf</option>
            <option value="clear">leeren</option>
          </select>
          {a.minutes !== null && <DurationInput label="Schätzung" minutes={a.minutes || 60} units={DURATION_UNITS.slice(0, 2)} onChange={(minutes) => onChange({ ...a, minutes })} />}
        </>
      )}
      {a.type === 'setPriority' && (
        <select aria-label="Priorität" className={sel} value={a.value} onChange={(e) => onChange({ ...a, value: Number(e.target.value) })}>
          {PRIORITIES.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      )}
      {a.type === 'notify' && (
        <div className="flex w-full flex-col gap-2">
          <input aria-label="Nachricht" className={cx(sel, 'min-w-0 flex-1')} maxLength={200} value={a.text} placeholder="Text der Nachricht" onChange={(e) => onChange({ ...a, text: e.target.value })} required />
          <RecipientsEditor a={a} s={s} onChange={onChange} />
        </div>
      )}
      {a.type === 'addChecklist' && (
        <textarea
          aria-label="Checklistenpunkte (einer pro Zeile)"
          className={cx(sel, 'min-h-20 w-full py-1.5')}
          value={a.items.join('\n')}
          placeholder="Ein Punkt pro Zeile"
          onChange={(e) => onChange({ ...a, items: e.target.value.split('\n') })}
        />
      )}
    </>
  );
}

/* ---------- Editor ---------- */

function RuleEditor({ snapshot, initial, onCancel, onSaved }: { snapshot: BoardSnapshot; initial: Automation | null; onCancel: () => void; onSaved: () => void }) {
  const ctx = useDescribeContext(snapshot);
  const cols = useMemo(() => [...snapshot.columns].sort(byPosition), [snapshot.columns]);
  const [name, setName] = useState(initial?.name ?? '');
  const [trigger, setTrigger] = useState<AutomationTrigger>(initial?.trigger ?? { type: 'moved', columnId: cols.at(-1)?.id ?? '' });
  const [when, setWhen] = useState<ConditionGroup>(() => upgradeGroup(initial?.when ?? { op: 'all', items: [] }));
  const [actions, setActions] = useState<AutomationAction[]>(initial?.actions ?? [{ type: 'complete' }]);
  const [once, setOnce] = useState(initial?.once ?? false);
  const [missing, setMissing] = useState<string[]>([]);
  const applyTemplate = (id: string) => {
    const t = RULE_TEMPLATES.find((x) => x.id === id);
    if (!t) return;
    const { draft, missing: m } = t.build({ columns: cols, labels: snapshot.labels.map((l) => ({ id: l.id, name: l.name || l.color })), fields: snapshot.fields });
    setName(draft.name);
    setTrigger(draft.trigger);
    setWhen(draft.when);
    setActions(draft.actions);
    setOnce(!!draft.once);
    setMissing(m);
    setPreview(null);
  };
  const [busy, setBusy] = useState(false);
  const [testCard, setTestCard] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const openCards = useMemo(() => snapshot.cards.filter((c) => !c.archivedAt).sort((a, b) => a.title.localeCompare(b.title, 'de')), [snapshot.cards]);

  const cleanActions = actions.map((a) => (a.type === 'addChecklist' ? { ...a, items: a.items.map((x) => x.trim()).filter(Boolean) } : a));
  const state = trigger.type === 'stateMet';
  const sentence = describeAutomation({ trigger, when, actions: cleanActions, once: state && once }, ctx);
  const total = when.items.reduce((n, x) => n + (isGroup(x) ? x.items.length : 1), 0);
  const body = { name: name.trim() || sentence.slice(0, 80), schemaVersion: 2, mode: state ? 'state' : 'event', once: state && once, trigger, when, actions: cleanActions };

  const move = (i: number, d: -1 | 1) =>
    setActions((l) => {
      const n = [...l];
      const [x] = n.splice(i, 1);
      n.splice(i + d, 0, x!);
      return n;
    });

  return (
    <form
      className="flex flex-col gap-4 p-4"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          if (initial) {
            await api.patch(`/api/automations/${initial.id}`, { ...body, expectedRevision: initial.revision });
            toast.info('Regel gespeichert.');
          } else {
            const r = await api.post<{ matching?: number }>(`/api/boards/${snapshot.board.id}/automations`, body);
            toast.info(
              r.matching
                ? `Regel gespeichert. ${r.matching === 1 ? '1 Karte passt' : `${r.matching} Karten passen`} schon – sie bleiben unberührt, bis du „Auf bestehende Karten anwenden“ wählst.`
                : 'Regel gespeichert.',
            );
          }
          onSaved();
        } catch (err) {
          toast.error((err as Error).message);
          if (err instanceof ApiError && err.status === 409) onSaved();
        } finally {
          setBusy(false);
        }
      }}
    >
      <button type="button" onClick={onCancel} className="inline-flex min-h-11 items-center gap-1 self-start text-sm text-[var(--ink-soft)] hover:text-[var(--ink)] sm:min-h-9">
        <ArrowLeft size={16} /> Zurück zur Liste
      </button>
      {initial && initial.problems.length > 0 && (
        <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100" role="alert">
          <Wrench size={15} className="mt-0.5 shrink-0" aria-hidden /> {initial.problems.join(' ')} Bitte die markierten Stellen neu wählen.
        </p>
      )}
      {!initial && (
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          <span className="flex items-center gap-1.5">
            <LayoutTemplate size={15} aria-hidden /> Aus Vorlage (optional)
          </span>
          <select className={sel} value="" onChange={(e) => applyTemplate(e.target.value)}>
            <option value="">Vorlage wählen …</option>
            {RULE_TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} – {t.description}
              </option>
            ))}
          </select>
        </label>
      )}
      {missing.length > 0 && (
        <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100" role="status">
          <Wrench size={15} className="mt-0.5 shrink-0" aria-hidden /> Bitte noch wählen: {missing.join(', ')}.
        </p>
      )}
      <label className="flex flex-col gap-1.5 text-sm font-medium">
        Name
        <input className={sel} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="z. B. Prüfbereit markieren" />
      </label>

      <fieldset className="flex min-w-0 flex-col gap-2 rounded-xl border border-[var(--line)] p-3">
        <legend className="px-1 text-sm font-semibold">Wann?</legend>
        <TriggerEditor t={trigger} s={snapshot} onChange={setTrigger} once={once} onOnce={setOnce} />
      </fieldset>

      <fieldset className="flex min-w-0 flex-col gap-2 rounded-xl border border-[var(--line)] p-3">
        <legend className="px-1 text-sm font-semibold">Unter welchen Bedingungen?</legend>
        {!when.items.length && <p className="text-sm text-[var(--ink-soft)]">Ohne Bedingung – die Regel läuft bei jedem Auslöser.</p>}
        <GroupEditor g={when} s={snapshot} total={total} onChange={setWhen} />
        {total >= AUTOMATION_MAX_PREDICATES && <p className="text-xs text-[var(--ink-soft)]">Höchstens {AUTOMATION_MAX_PREDICATES} Bedingungen.</p>}
      </fieldset>

      <fieldset className="flex min-w-0 flex-col gap-2 rounded-xl border border-[var(--line)] p-3">
        <legend className="px-1 text-sm font-semibold">Was soll passieren?</legend>
        <ol className="flex flex-col gap-3">
          {actions.map((a, i) => (
            <li key={i} className="flex flex-wrap items-start gap-2 border-b border-[var(--line)] pb-3 last:border-b-0 last:pb-0">
              <span className="inline-flex size-11 shrink-0 items-center justify-center text-sm font-semibold text-[var(--ink-soft)] sm:size-9" aria-hidden>
                {i + 1}.
              </span>
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <ActionEditor a={a} s={snapshot} n={i + 1} onChange={(x) => setActions((l) => l.map((y, j) => (j === i ? x : y)))} />
              </div>
              <span className="flex shrink-0">
                <button type="button" aria-label={`${i + 1}. Aktion nach oben`} disabled={i === 0} className={cx(iconBtn, 'disabled:opacity-30')} onClick={() => move(i, -1)}>
                  <ArrowUp size={16} />
                </button>
                <button type="button" aria-label={`${i + 1}. Aktion nach unten`} disabled={i === actions.length - 1} className={cx(iconBtn, 'disabled:opacity-30')} onClick={() => move(i, 1)}>
                  <ArrowDown size={16} />
                </button>
                {actions.length > 1 && (
                  <button type="button" aria-label={`${i + 1}. Aktion entfernen`} className={cx(iconBtn, 'hover:text-rose-600')} onClick={() => setActions((l) => l.filter((_, j) => j !== i))}>
                    <X size={16} />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ol>
        {actions.length < AUTOMATION_MAX_ACTIONS && (
          <button type="button" className="inline-flex min-h-11 items-center gap-1 self-start text-sm text-accent-fg sm:min-h-9" onClick={() => setActions((l) => [...l, { type: 'setPriority', value: 3 }])}>
            <Plus size={15} /> Weitere Aktion
          </button>
        )}
      </fieldset>

      <p className="rounded-lg bg-violet-50 p-3 text-sm text-violet-950 dark:bg-violet-950/40 dark:text-violet-100" aria-live="polite">
        <Zap size={14} className="mr-1 inline" aria-hidden />
        {sentence}
      </p>

      <section className="flex flex-col gap-2 rounded-xl border border-dashed border-[var(--line)] p-3" aria-labelledby="rule-test">
        <h3 id="rule-test" className="flex items-center gap-1.5 text-sm font-semibold">
          <FlaskConical size={15} aria-hidden /> An Karte prüfen
        </h3>
        <p className="text-xs text-[var(--ink-soft)]">Zeigt, welche Bedingungen zutreffen und was passieren würde – ohne etwas zu ändern oder zu versenden.</p>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Karte zum Prüfen" className={cx(sel, 'min-w-0 flex-1')} value={testCard} onChange={(e) => (setTestCard(e.target.value), setPreview(null))}>
            <option value="">Karte wählen …</option>
            {openCards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
          <Button
            type="button"
            disabled={!testCard}
            onClick={async () => {
              try {
                setPreview(await api.post<Preview>(`/api/boards/${snapshot.board.id}/automations/preview`, { cardId: testCard, rule: body }));
              } catch (err) {
                toast.error((err as Error).message);
              }
            }}
          >
            Prüfen
          </Button>
        </div>
        {preview && (
          <div className="flex flex-col gap-2 text-sm" aria-live="polite">
            {preview.problems.length > 0 && <p className="text-amber-800 dark:text-amber-200">Nicht auswertbar: {preview.problems.join(' ')}</p>}
            {preview.conditions.length > 0 && (
              <ul className="flex flex-col gap-1">
                {preview.conditions.map((c, i) => (
                  <li key={i} className={cx('flex items-start gap-1.5', c.depth > 0 && 'pl-5')}>
                    {c.ok ? <Check size={15} className="mt-0.5 shrink-0 text-emerald-600" aria-label="erfüllt" /> : <X size={15} className="mt-0.5 shrink-0 text-rose-600" aria-label="nicht erfüllt" />}
                    <span>
                      {c.text}
                      {c.note && <span className="text-[var(--ink-soft)]"> – {c.note}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {preview.stateNote && <p className="text-[var(--ink-soft)]">{preview.stateNote}</p>}
            {preview.timing && <p className="text-[var(--ink-soft)]">{preview.timing}</p>}
            <p className="font-medium">
              {preview.archived
                ? 'Die Karte ist archiviert – Regeln laufen dort nicht.'
                : preview.conditionsMet
                  ? state
                    ? 'Bedingungen erfüllt – beim Eintritt in diesen Zustand würde die Regel ausführen:'
                    : 'Bedingungen erfüllt – beim Auslöser würde die Regel laufen:'
                  : 'Bedingungen nicht erfüllt – die Regel würde nicht laufen.'}
            </p>
            {preview.conditionsMet && !preview.archived && (
              <ul className="flex flex-col gap-1">
                {preview.actions.map((a, i) => (
                  <li key={i} className="flex flex-wrap gap-x-1.5">
                    <span>
                      {i + 1}. {a.text}
                    </span>
                    {a.result && <span className="font-medium">{a.result}</span>}
                    {a.recipients && <span className="w-full pl-4 text-[var(--ink-soft)]">An: {a.recipients.length ? a.recipients.join(', ') : 'niemanden (derzeit keine Empfänger)'}</span>}
                    {a.note && <span className="w-full pl-4 text-[var(--ink-soft)]">{a.note}</span>}
                    <span className="text-[var(--ink-soft)]">
                      {a.result?.startsWith('Wartet') || a.result?.startsWith('Ein ganztägiger') ? '' : a.changes === true ? '– ändert die Karte' : a.changes === false ? '– keine Änderung (schon so)' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {!!preview.warnings?.length && (
              <ul className="flex flex-col gap-1 rounded-lg bg-amber-50 p-2 text-amber-900 dark:bg-amber-950 dark:text-amber-100" aria-label="Hinweise">
                {preview.warnings.map((w) => (
                  <li key={w} className="flex items-start gap-1.5">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden /> {w}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <div className="flex justify-end gap-2">
        <Button type="button" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button type="submit" variant="primary" disabled={busy}>
          Speichern
        </Button>
      </div>
    </form>
  );
}
